// src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  TextField,
  Button,
  Grid,
  CircularProgress,
  MenuItem,
  Select,
  Alert,
  Stack,
  LinearProgress,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Person,
  AddPhotoAlternateRounded,
  DeleteOutlineRounded,
  LockReset as LockResetIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { useMe } from "../../hooks/useMe";
import { useQuery, useQueryClient } from "react-query";
import { useThemeContext } from "../../context/ThemeContext";
import { useTranslatedThemeOptions } from "../../hooks/useTranslatedThemeOptions";
import { AuthContext } from "../../context/AuthContext";
import { useS3Upload } from "../../hooks/useS3Upload";
import { fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";
import { setUiLocale, resolveUiLocaleCode, getGlobalLocalesForUiSelect } from "../../i18n/config";
import { readPrimaryProjectId } from "../../utils/primaryProjectId";
import NotificationPreferencesProfileSection from "../../components/notifications/NotificationPreferencesProfileSection";
import PersonalWhatsappProfileSection from "./PersonalWhatsappProfileSection";

/* ========= helpers ========= */
const CARD = { p: 3, borderRadius: 3, height: "100%" };
const MAX_AVATAR_MB = 10;

const SectionTitle = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
    <Typography variant="h6">{children}</Typography>
  </Box>
);

// unwrap { message, data } -> data
const unwrap = (d) => (d && typeof d === "object" && "data" in d ? d.data : d);

// validações (baseadas no AddUserForm)
const onlyDigits = (s) => String(s || "").replace(/\D+/g, "");
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
function maskPhoneBR(s) {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
}
const isValidPhoneBR = (s) => {
  const d = onlyDigits(s);
  return d.length === 10 || d.length === 11;
};

export default function ProfilePage() {
  const { t } = useTranslation("profile");
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { me, save } = useMe();
  const { changeTheme, currentTheme } = useThemeContext();
  const themeOptions = useTranslatedThemeOptions();
  const { userData, updateUserData } = useContext(AuthContext);
  const { uploadOne } = useS3Upload();

  // -------- form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [celular, setCelular] = useState("");
  const [preferences, setPreferences] = useState(currentTheme); // key do tema selecionado
  const [cargoNome, setCargoNome] = useState("");
  const [incluirNome, setIncluirNome] = useState(false); // incluir nome em mensagens WhatsApp (incluir_nome = 1)

  // avatar
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // UX
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [secState, setSecState] = useState({ loading: false, ok: false, err: "" }); // Segurança
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMsg, setThemeMsg] = useState({ ok: false, err: "" });
  const [localeUi, setLocaleUi] = useState("pt-BR");
  const [localeSaving, setLocaleSaving] = useState(false);
  const [localeMsg, setLocaleMsg] = useState({ ok: false, err: "" });

  const primaryProjectId = readPrimaryProjectId();

  const { data: wsLangsPayload } = useQuery(
    ["i18n-workspace-languages", "profile", primaryProjectId],
    async () => {
      if (!primaryProjectId) return { items: [] };
      const res = await fetchData(
        `/i18n/workspace-languages?id_projeto=${encodeURIComponent(primaryProjectId)}`,
        "GET"
      );
      const items = res?.data?.items ?? [];
      return { items: Array.isArray(items) ? items : [] };
    },
    { enabled: Boolean(primaryProjectId), staleTime: 60 * 1000, refetchOnWindowFocus: true }
  );

  const localeMenuOptions = useMemo(() => {
    const globals = getGlobalLocalesForUiSelect().map((x) => ({
      code: x.code,
      label: x.nativeName,
    }));
    const globalCodes = new Set(globals.map((g) => g.code));
    const pid = Number(primaryProjectId);
    const rows = wsLangsPayload?.items || [];
    const customs = rows.filter((row) => {
      if (!row?.code) return false;
      if (row.is_active === 0 || row.is_active === false) return false;
      if (globalCodes.has(row.code)) return false;
      const p = row.id_projeto;
      if (p == null || Number(p) === 0) return true;
      return Number(p) === pid;
    });
    const customOpts = customs.map((row) => ({
      code: row.code,
      label: `${row.label || row.code} (${row.code})`,
    }));
    return [...globals, ...customOpts];
  }, [wsLangsPayload, primaryProjectId]);

  const localeMenuOptionsWithCurrent = useMemo(() => {
    const opts = [...localeMenuOptions];
    const codes = new Set(opts.map((o) => o.code));
    const cur = String(localeUi || "").trim();
    if (cur && !codes.has(cur)) {
      opts.push({ code: cur, label: cur });
    }
    return opts;
  }, [localeMenuOptions, localeUi]);

  const loading = me.isLoading || me.isFetching;
  const saving = save.isLoading;

  // dados normalizados do endpoint /users/me
  const userFromMe = unwrap(me?.data) || me?.data || {};
  const effectiveAvatarSrc =
    avatarPreview || userFromMe.imagem || userData?.imagem || "";

  // --- Hidratação apenas na 1ª carga (só “tranca” quando a resposta tiver incluir_nome, para não travar com cache antigo)
  const hydratedRef = useRef(false);
  useEffect(() => {
    const user = userFromMe || null;
    if (!user || !user.id) return;
    if (hydratedRef.current) return;

    const initialImage = user.imagem || userData?.imagem || "";

    setName(user.nome || userData?.nome || "");
    setEmail(String(user.email || userData?.email || "").trim().toLowerCase());
    setCelular(user.celular ? maskPhoneBR(user.celular) : userData?.celular ? maskPhoneBR(userData.celular) : "");
    setAvatarUrl(initialImage);
    setAvatarPreview(initialImage);
    setPreferences(user.tema || currentTheme);
    setCargoNome(user.cargo_nome || "");
    setIncluirNome(Number(user.incluir_nome) === 1);

    // Só considera hidratação concluída quando a API devolver incluir_nome (evita travar com cache sem o campo)
    if (Object.prototype.hasOwnProperty.call(user, "incluir_nome")) {
      hydratedRef.current = true;
    }
  }, [userFromMe, userData]); // importante: sem currentTheme aqui

  useEffect(() => {
    const user = userFromMe || null;
    if (!user?.id) return;
    setLocaleUi(resolveUiLocaleCode(user, userData));
  }, [userFromMe, userData?.locale_code, userData?.id]);

  // garante que o e-mail seja preenchido quando vier do /me,
  // mas sem sobrescrever caso o usuário já esteja editando
  useEffect(() => {
    const user = userFromMe || null;
    if (!user) return;
    setEmail((prev) =>
      prev
        ? prev
        : String(user.email || userData?.email || "").trim().toLowerCase()
    );
  }, [userFromMe, userData]);

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith?.("blob:")) URL.revokeObjectURL(avatarPreview);
    };
  }, [avatarPreview]);

  // -------- validação
  const computeErrors = (state) => {
    const errs = {};
    const nome = String(state.name || "").trim();
    const mail = String(state.email || "").trim();

    if (!nome) errs.name = "Campo obrigatório";
    else if (nome.length < 2) errs.name = "Informe ao menos 2 caracteres";

    if (!mail) errs.email = "Campo obrigatório";
    else if (!emailRegex.test(mail)) errs.email = "E-mail inválido";

    if (state.celular && !isValidPhoneBR(state.celular)) errs.celular = "Informe um celular válido";

    return errs;
  };
  const validate = () => {
    const errs = computeErrors({ name, email, celular });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const showError = (f) => Boolean((touched[f] || saving) && errors[f]);
  const help = (f) => (showError(f) ? errors[f] : " ");

  // -------- avatar handlers
  const onFiles = (filesList) => {
    const file = Array.from(filesList || [])[0];
    if (!file) return;
    if (!/^image\//i.test(file.type || "")) return alert("Selecione uma imagem válida.");
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) return alert(`Imagem maior que ${MAX_AVATAR_MB}MB.`);
    const preview = URL.createObjectURL(file);
    setAvatarFile(file);
    setAvatarPreview(preview);
  };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); onFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handlePick = (e) => onFiles(e.target.files);
  const removeAvatar = () => {
    if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview("");
    setAvatarUrl("");
  };
  const buildPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    const uid = userData?.id || "me";
    return `workspace-${workspace}/users/avatars/${yyyy}/${mm}/user-${uid}`;
  };

  // -------- salvar cadastro (sem tema; tema tem fluxo próprio)
  const canSave = useMemo(() => !saving && String(name).trim() && String(email).trim(), [name, email, saving]);

  const handleSave = async () => {
    setTouched({ name: true, email: true, celular: true });
    if (!validate()) return;

    try {
      let finalUrl = avatarUrl;
      if (avatarFile) {
        setUploading(true);
        const { url } = await uploadOne(avatarFile, { prefix: buildPrefix(), customName: avatarFile.name });
        finalUrl = url;
        setUploading(false);
      }

      const payload = {
        nome: String(name).trim(),
        email: String(email).trim().toLowerCase(),
        celular: onlyDigits(celular) || null,
        imagem: finalUrl || null,
        incluir_nome: incluirNome ? 1 : 0,
        // tema removido: salvo no box "Tema"
      };
      await save.mutateAsync(payload);

      setAvatarUrl(finalUrl);
      if (avatarPreview?.startsWith("blob:")) URL.revokeObjectURL(avatarPreview);
      setAvatarFile(null);
      setAvatarPreview(finalUrl || "");

      // sincroniza AuthContext/localStorage para refletir imediatamente no header/topbar
      if (typeof updateUserData === "function") {
        updateUserData({
          nome: String(name).trim(),
          email: String(email).trim().toLowerCase(),
          celular: onlyDigits(celular) || null,
          imagem: finalUrl || null,
          incluir_nome: incluirNome ? 1 : 0,
        });
      }
    } catch (e) {
      setUploading(false);
      alert(e?.message || "Erro ao salvar perfil.");
    }
  };

  // -------- tema: salvar imediatamente ao mudar
  const handleLocaleChange = async (nextLocale) => {
    const next = String(nextLocale || "").trim() || "pt-BR";
    setLocaleUi(next);
    setUiLocale(next);
    setLocaleSaving(true);
    setLocaleMsg({ ok: false, err: "" });
    try {
      await fetchData("/users/me/locale", "PATCH", { locale_code: next });
      if (typeof updateUserData === "function") updateUserData({ locale_code: next });
      queryClient.invalidateQueries(["me"]);
      setLocaleMsg({ ok: true, err: "" });
      setTimeout(() => setLocaleMsg({ ok: false, err: "" }), 2500);
    } catch (e) {
      setLocaleMsg({ ok: false, err: e?.message || "Falha ao salvar idioma." });
    } finally {
      setLocaleSaving(false);
    }
  };

  const handleThemeChangeImmediate = async (nextTheme) => {
    const prev = preferences;
    setPreferences(nextTheme);
    changeTheme(nextTheme); // aplica instantaneamente

    setThemeSaving(true);
    setThemeMsg({ ok: false, err: "" });
    try {
      await save.mutateAsync({ tema: String(nextTheme) }); // salva apenas o tema
      setThemeSaving(false);
      setThemeMsg({ ok: true, err: "" });
      setTimeout(() => setThemeMsg({ ok: false, err: "" }), 2500);
    } catch (e) {
      // reverte visual e estado se falhar
      setThemeSaving(false);
      setThemeMsg({ ok: false, err: e?.response?.data?.message || "Falha ao salvar tema." });
      setPreferences(prev);
      changeTheme(prev);
    }
  };

  // -------- segurança: enviar reset de senha
  const handleSendReset = async () => {
    const user = unwrap(me?.data) || me?.data || null;
    if (!user?.id) return;
    try {
      setSecState({ loading: true, ok: false, err: "" });
      await fetchData(`/users/${user.id}/send-reset`, "POST");
      setSecState({ loading: false, ok: true, err: "" });
    } catch (e) {
      setSecState({ loading: false, ok: false, err: e?.response?.data?.message || "Falha ao enviar e-mail." });
    }
  };

  return (
    <Box
      className="tour-profile-page"
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
            : "linear-gradient(180deg, #fafafa, #f3f3f3)",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>{t("title")}</Typography>
        {(loading || saving || uploading) && <CircularProgress size={22} />}
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        {/* Coluna principal: identidade + WhatsApp pessoal (evita corte na coluna estreita) */}
        <Grid item xs={12} md={7}>
          <Stack spacing={2}>
          <Paper sx={CARD}>
            <SectionTitle>{t("identity.section")}</SectionTitle>

            {/* Avatar grande + upload */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                p: 2,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 2,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              }}
            >
              <Avatar
                src={effectiveAvatarSrc || undefined}
                sx={{ width: 112, height: 112, bgcolor: "action.hover" }}
              >
                {!effectiveAvatarSrc && <Person sx={{ fontSize: 56 }} />}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("identity.photoTitle")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("identity.photoHint", { maxMb: MAX_AVATAR_MB })}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddPhotoAlternateRounded />}
                    component="label"
                    disabled={uploading}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {t("identity.upload")}
                    <input type="file" accept="image/*" hidden onChange={handlePick} />
                  </Button>
                  {(avatarPreview || avatarUrl) && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<DeleteOutlineRounded />}
                      onClick={removeAvatar}
                      disabled={uploading}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {t("identity.remove")}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Box>

            {uploading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Campos */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("identity.name")}
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  error={showError("name")}
                  helperText={help("name")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("identity.email")}
                  type="email"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => {
                    setEmail((v) => String(v || "").trim().toLowerCase());
                    setTouched((t) => ({ ...t, email: true }));
                  }}
                  error={showError("email")}
                  helperText={help("email")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("identity.phone")}
                  fullWidth
                  value={celular}
                  onChange={(e) => setCelular(maskPhoneBR(e.target.value))}
                  onBlur={() => setTouched((t) => ({ ...t, celular: true }))}
                  error={showError("celular")}
                  helperText={help("celular")}
                  placeholder="(11) 91234-5678"
                  inputMode="numeric"
                />
              </Grid>
            </Grid>

            {/* Incluir nome nas mensagens WhatsApp */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!incluirNome}
                    onChange={(e) => setIncluirNome(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("identity.whatsappNameSwitch")}
                  </Typography>
                }
              />
            </Box>

            {/* Cargo somente leitura */}
            {cargoNome ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">{t("identity.roleLabel")}</Typography>
                <Chip size="small" label={cargoNome} />
              </Stack>
            ) : null}

            {/* Ações */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => {
                  if (!validate()) { setTouched({ name: true, email: true, celular: true }); return; }
                  handleSave();
                }}
                disabled={!canSave || uploading}
                sx={{ minWidth: 180, textTransform: "none", fontWeight: 800 }}
              >
                {saving ? t("identity.saving") : t("identity.saveChanges")}
              </Button>
            </Box>
          </Paper>

          <PersonalWhatsappProfileSection />
          </Stack>
        </Grid>

        {/* Coluna secundária */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Paper sx={CARD}>
              <SectionTitle>{t("language.section")}</SectionTitle>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t("language.hint")}
              </Typography>
              <Select
                fullWidth
                value={localeUi}
                onChange={(e) => handleLocaleChange(e.target.value)}
                disabled={localeSaving}
                sx={{ mb: 1 }}
              >
                {localeMenuOptionsWithCurrent.map(({ code, label }) => (
                  <MenuItem key={code} value={code}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {localeSaving && <LinearProgress sx={{ mb: 1 }} />}
              {localeMsg.err && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {localeMsg.err}
                </Alert>
              )}
              {localeMsg.ok && !localeMsg.err && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {t("language.saved")}
                </Alert>
              )}
            </Paper>

            <Paper sx={CARD}>
              <SectionTitle>{t("theme.section")}</SectionTitle>

              <Select
                fullWidth
                value={preferences || ""}
                onChange={(e) => handleThemeChangeImmediate(String(e.target.value || ""))}
                sx={{ mb: 1 }}
              >
                {themeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>

              {themeSaving && <LinearProgress sx={{ mb: 1 }} />}

              {themeMsg.err && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {themeMsg.err}
                </Alert>
              )}
              {themeMsg.ok && !themeMsg.err && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {t("theme.updated")}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary">
                {t("theme.hint")}
              </Typography>
            </Paper>

            <NotificationPreferencesProfileSection />

            <Paper sx={CARD}>
              <SectionTitle>{t("security.section")}</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("security.hint")}
              </Typography>

              {secState.err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {secState.err}
                </Alert>
              )}
              {secState.ok && !secState.err && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("security.emailSent")}
                </Alert>
              )}

              <Button
                variant="outlined"
                onClick={handleSendReset}
                startIcon={<LockResetIcon />}
                disabled={secState.loading}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {secState.loading ? t("security.sending") : t("security.sendEmail")}
              </Button>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
